import { MediaLibDialogCtxProvider } from "@editor/modules/media-lib/ctx";
import { useMediaLibDialogSubmit } from "@editor/modules/media-lib/hooks";
import { MediaDialog } from "@editor/modules/media-lib/modules/media-dialog";
import {
  MediaLibPresetEnum,
  MediaPanelTabEnum,
  type IMediaLibDialogResponse,
} from "@editor/modules/media-lib/types";
import { cn } from "@lib/cn";
import { useAppDispatch } from "@store";
import { closeDialog } from "@store/dialogs";
import { useEffect } from "react";
import { MEDIA_LIB_TAB_STORAGE_KEY } from "../constants";
import { useMediaLibDialogStoreShallow } from "../stores";
import { MediaLibDialogMultiSelectPopover } from "./media-lib-dialog-multi-select-popover";

const DIALOG_ID = "media-lib";

interface IProps {
  preset?: MediaLibPresetEnum;
  source?: "default" | "settings";
  onSelect?: (items: IMediaLibDialogResponse) => void;
  onMultiSelect?: (items: IMediaLibDialogResponse[]) => void;
  category?: MediaPanelTabEnum;
  insertElementOnSelect?: boolean;
}

export type IMediaLibDialogProps = IProps;

export const MediaLibDialog = ({
  preset = MediaLibPresetEnum.All,
  source = "default",
  onMultiSelect,
  onSelect,
  insertElementOnSelect = false,
  category,
}: IProps) => {
  const dispatch = useAppDispatch();

  const { reset, setSelectedTab } = useMediaLibDialogStoreShallow((s) => ({
    reset: s.reset,
    setSelectedTab: s.setSelectedTab,
  }));

  useEffect(() => {
    const tab = getSelectedTab(preset, category);
    if (tab) setSelectedTab(tab);
    return () => reset();
  }, []);

  const handleClose = () => {
    dispatch(closeDialog(DIALOG_ID));
  };

  const { handleSelect, handleMultiSelect } = useMediaLibDialogSubmit({
    insertElementOnSelect,
    onSelect,
    onMultiSelect,
    onClose: handleClose,
  });

  const isMultiSelect = !!onMultiSelect;
  return (
    <MediaLibDialogCtxProvider
      ctxState={{
        preset,
        isMultiSelect,
        isSingleSelect: !!onSelect,
        insertElementOnSelect,
        canBulkActions: !onSelect,
        onSelect: handleSelect,
        onMultiSelect: handleMultiSelect,
        closeDialog: handleClose,
        source,
      }}
    >
      <div
        className={cn(
          "tw-relative tw-h-[calc(100vh-16px)] tw-max-h-[800px] tw-w-[calc(100vw-16px)] tw-max-w-[600px] tw-rounded-lg tw-bg-white tw-shadow-main",
          preset === MediaLibPresetEnum.All && "tw-max-w-[680px]",
          // Narrow viewports (chrome-extension side panel etc.): fill the
          // available space rather than getting clipped at a desktop width.
          // Force overflow-hidden so nothing inside can escape the dialog box.
          "-sm:tw-h-screen -sm:tw-max-h-full -sm:tw-w-screen -sm:tw-max-w-full -sm:tw-rounded-none -sm:tw-overflow-hidden",
        )}
      >
        <MediaDialog preset={preset} />
        {isMultiSelect && <MediaLibDialogMultiSelectPopover />}
      </div>
    </MediaLibDialogCtxProvider>
  );
};

MediaLibDialog.dialogId = DIALOG_ID;

const isValidTab = (tab: string | null): tab is MediaPanelTabEnum => {
  if (!tab) return false;
  return [
    MediaPanelTabEnum.StockImages,
    MediaPanelTabEnum.StockVideos,
    MediaPanelTabEnum.MediaLib,
    MediaPanelTabEnum.LogoFinder,
    MediaPanelTabEnum.Gifs,
    MediaPanelTabEnum.AiGenerate,
  ].includes(tab as MediaPanelTabEnum);
};

const getSelectedTab = (
  preset: MediaLibPresetEnum,
  category?: MediaPanelTabEnum,
): MediaPanelTabEnum | undefined => {
  const storedTab = category || localStorage.getItem(MEDIA_LIB_TAB_STORAGE_KEY);
  const tab = isValidTab(storedTab) ? storedTab : undefined;

  switch (preset) {
    case MediaLibPresetEnum.OnlyImages:
      // Return tab if it's an image tab, otherwise default to StockImages
      return tab && tab !== MediaPanelTabEnum.StockVideos
        ? tab
        : MediaPanelTabEnum.StockImages;

    case MediaLibPresetEnum.OnlyVideos:
      // Return tab if it's a video tab, otherwise default to StockVideos
      return tab &&
        (tab === MediaPanelTabEnum.StockVideos ||
          tab === MediaPanelTabEnum.MediaLib)
        ? tab
        : MediaPanelTabEnum.StockVideos;

    default:
      return tab;
  }
};
